import { Link } from '@tanstack/react-router';
import {
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  PanelLeftClose,
  Search,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { scrollWithinPane } from '@/shared/lib/scroll-within-pane';
import { pathLabel } from '@/shared/program/path-label';
import { ACTIVITY_KIND, activityHref } from '@/shared/ui/activity/activity-kind';

import type { StudioModel } from '@/shared/program/use-studio';

/** The full curriculum stays in view as a searchable, expandable IDE-style explorer. */
export function StudioNav({
  studio,
  activeId,
  onNavigate,
  onHide,
  explorerId,
}: {
  studio: StudioModel;
  activeId: string | null;
  onNavigate?: () => void;
  /** Collapses the persistent explorer; the control belongs to the panel it hides. */
  onHide?: () => void;
  explorerId?: string;
}) {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  const activeLink = useRef<HTMLAnchorElement>(null);
  const activeModule = studio.sequence.find((item) => item.id === activeId)?.moduleNumber;
  const normalized = query.trim().toLowerCase();
  const modules = studio.modules
    .map((module) => {
      const moduleMatches =
        !normalized ||
        `Module ${module.number} Module ${String(module.number).padStart(2, '0')} ${module.title}`
          .toLowerCase()
          .includes(normalized);
      return {
        ...module,
        matching: module.items.filter(
          (item) =>
            moduleMatches ||
            `${item.code} ${ACTIVITY_KIND[item.kind].label} ${item.title} ${item.summary}`
              .toLowerCase()
              .includes(normalized),
        ),
      };
    })
    .filter((module) => !normalized || module.matching.length > 0);
  const allCollapsed = collapsed.size === studio.modules.length;

  useEffect(() => {
    if (activeModule !== undefined) {
      setCollapsed((current) => {
        if (!current.has(activeModule)) return current;
        const next = new Set(current);
        next.delete(activeModule);
        return next;
      });
    }
  }, [activeModule, activeId]);

  useEffect(() => {
    if (activeLink.current) scrollWithinPane(activeLink.current, { block: 'nearest' });
  }, [activeId, collapsed]);

  function toggleModule(number: number) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  }

  return (
    <div className="curriculum-navigation bg-sidebar/50 flex h-full min-h-0 flex-col">
      <div className="border-line shrink-0 border-b px-4 pb-3 pt-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-fg text-sm font-medium">Curriculum</h2>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() =>
                setCollapsed(
                  allCollapsed ? new Set() : new Set(studio.modules.map((module) => module.number)),
                )
              }
              aria-label={allCollapsed ? 'Expand all modules' : 'Collapse all modules'}
              title={allCollapsed ? 'Expand all modules' : 'Collapse all modules'}
              className="text-fg-muted hover:bg-surface flex size-8 items-center justify-center rounded"
            >
              {allCollapsed ? (
                <ChevronsUpDown className="size-3.5" />
              ) : (
                <ChevronsDownUp className="size-3.5" />
              )}
            </button>
            {onHide ? (
              <button
                type="button"
                onClick={onHide}
                aria-label="Hide curriculum"
                title="Hide curriculum"
                aria-expanded="true"
                aria-controls={explorerId}
                className="text-fg-muted hover:bg-surface flex size-8 items-center justify-center rounded"
              >
                <PanelLeftClose className="size-4" />
              </button>
            ) : null}
          </div>
        </div>
        <label className="border-line bg-canvas flex items-center gap-2 rounded border px-2.5">
          <Search className="text-fg-subtle size-3.5" aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Find an activity"
            placeholder="Search curriculum…"
            className="text-fg h-8 w-full min-w-0 bg-transparent text-[12px] outline-none"
          />
        </label>
      </div>
      <nav
        aria-label="Module navigation"
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-3"
      >
        <ol className="space-y-3">
          {modules.map((module) => {
            const open = Boolean(normalized) || !collapsed.has(module.number);
            const label = pathLabel(module.path);
            return (
              <li key={module.number}>
                <button
                  type="button"
                  onClick={() => toggleModule(module.number)}
                  aria-expanded={open}
                  aria-label={`Module ${module.number}: ${module.title}${label ? ` (${label})` : ''}`}
                  className="text-fg hover:bg-surface flex w-full items-start gap-2 rounded px-2 py-2 text-left"
                >
                  <ChevronDown
                    className={cn(
                      'text-fg-muted mt-0.5 size-3.5 transition-transform',
                      !open && '-rotate-90',
                    )}
                  />
                  <span className="min-w-0 flex-1 text-sm font-medium leading-5">
                    <span className="text-fg-muted mb-1 block text-xs font-normal">
                      Module {module.number}
                      {label ? (
                        <span
                          className={cn(
                            'ml-1.5',
                            module.path === 'optional' ? 'text-fg-subtle' : 'text-accent',
                          )}
                        >
                          · {label}
                        </span>
                      ) : null}
                    </span>
                    {module.title}
                  </span>
                  <span className="text-fg-subtle pt-0.5 text-[12px] tabular-nums">
                    {module.done}/{module.items.length}
                    <span className="sr-only"> complete</span>
                  </span>
                </button>
                {open ? (
                  <ol className="border-line ml-3 border-l pl-2">
                    <li>
                      <Link
                        to="/studio/modules/$number"
                        params={{ number: String(module.number) }}
                        onClick={onNavigate}
                        className="text-fg-muted hover:text-fg block px-2 py-1.5 text-[12px]"
                      >
                        Module overview
                      </Link>
                    </li>
                    {module.matching.map((item) => {
                      const kind = ACTIVITY_KIND[item.kind];
                      const ActivityIcon = kind.icon;
                      return (
                        <li key={item.id}>
                          <Link
                            ref={item.id === activeId ? activeLink : undefined}
                            to={activityHref(item.kind, item.id)}
                            onClick={onNavigate}
                            aria-current={item.id === activeId ? 'page' : undefined}
                            className={cn(
                              'flex items-start gap-2 rounded px-2 py-2 text-sm leading-5',
                              item.id === activeId
                                ? 'bg-surface text-fg'
                                : 'text-fg-muted hover:bg-surface hover:text-fg',
                            )}
                          >
                            <ActivityIcon
                              className="text-fg-muted mt-0.5 size-4 shrink-0"
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block break-words">{item.title}</span>
                              <span className="text-fg-subtle mt-0.5 block text-[12px] leading-5">
                                {kind.label}
                              </span>
                            </span>
                            {item.status === 'complete' ? (
                              <Check
                                className="text-success mt-0.5 size-3.5 shrink-0"
                                aria-label="Complete"
                              />
                            ) : item.status === 'in_progress' ? (
                              <span
                                className="bg-accent mt-2 size-1.5 shrink-0 rounded-full"
                                aria-label="In progress"
                              />
                            ) : null}
                          </Link>
                        </li>
                      );
                    })}
                  </ol>
                ) : null}
              </li>
            );
          })}
        </ol>
        {modules.length === 0 ? (
          <p className="text-fg-muted px-3 py-8 text-[12px] leading-6">
            No activities found. Try another topic.
          </p>
        ) : null}
      </nav>
      <div className="border-line text-fg-subtle shrink-0 space-y-2 border-t px-4 py-3 text-[12px]">
        <p>
          {studio.done} of {studio.total} activities complete
        </p>
        <Link
          to="/studio/earlier"
          onClick={onNavigate}
          className="text-fg-muted hover:text-fg focus-visible:ring-ring inline-block rounded-sm outline-none focus-visible:ring-2"
        >
          Earlier workshops
        </Link>
      </div>
    </div>
  );
}
